// SPDX-FileCopyrightText: Copyright (C) 2026 provide.io llc
// SPDX-License-Identifier: Apache-2.0
import { type ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";
import { listRecent } from "@/lib/recent";

export function CommandPalette(): ReactElement {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
  const recent = listRecent();
  const go = (path: string): void => {
    setOpen(false);
    navigate(path);
  };
  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      {/* Visually-hidden title so the Radix Dialog passes accessibility
          checks (every dialog needs an accessible name). Screen readers
          read this; sighted users see the input placeholder. */}
      <DialogTitle className="sr-only">Command palette</DialogTitle>
      <CommandInput placeholder="Type a command or search…" />
      <CommandList>
        <CommandEmpty>No results.</CommandEmpty>
        <CommandGroup heading="Recent lookups">
          {recent.map((name) => (
            <CommandItem key={name} onSelect={() => go(`/d/${name}`)}>
              {name}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading="Navigation">
          {/* Lookup + Wildcard advertised; Registrars / Playground /
              Admin remain reachable by direct URL but aren't surfaced
              here so the palette stays focused on the public path. */}
          <CommandItem onSelect={() => go("/")}>Lookup</CommandItem>
          <CommandItem onSelect={() => go("/wildcard")}>Wildcard</CommandItem>
          <CommandItem onSelect={() => go("/pricing")}>Pricing</CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
